import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';

// Home Components
import SeasonalBanner from '../components/home/SeasonalBanner';
import Hero from '../components/home/Hero';
import HeroQuickActions from '../components/home/HeroQuickActions';
import CategoryStrip from '../components/home/CategoryStrip';
import BestSellers from '../components/home/BestSellers';
import PromotionsStrip from '../components/home/PromotionsStrip';
import StatsCounter from '../components/home/StatsCounter';
import AboutSection from '../components/home/AboutSection';
import MeetChef from '../components/home/MeetChef';
import WhyChooseUs from '../components/home/WhyChooseUs';
import DeliveryOptions from '../components/home/DeliveryOptions';
import EventsSection from '../components/home/EventsSection';
import Testimonials from '../components/home/Testimonials';
import LeaveReview from '../components/home/LeaveReview';
import GalleryPreview from '../components/home/GalleryPreview';
import FoodVideos from '../components/home/FoodVideos';
import ReservationCTA from '../components/home/ReservationCTA';
import OpeningHours from '../components/home/OpeningHours';
import LocationSection from '../components/home/LocationSection';
import FloatingActionMenu from '../components/FloatingActionMenu';

const Home = () => {
  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Analytics tracking hooks
  useEffect(() => {
    const handler = (e) => {
      const el = e.target.closest('[data-track]');
      if (el) console.log(`[Analytics] ${el.dataset.track}`);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div>
      <SeasonalBanner />
      <Hero />
      <HeroQuickActions />
      <CategoryStrip />
      <BestSellers />
      <PromotionsStrip />
      <StatsCounter />
      <AboutSection />
      <MeetChef />
      <WhyChooseUs />
      <DeliveryOptions />
      <EventsSection />
      <Testimonials />
      <LeaveReview />
      <GalleryPreview />
      <FoodVideos />
      <ReservationCTA />
      <OpeningHours />
      <LocationSection />

      {/* ===== FOOTER ===== */}
      <footer style={{ backgroundColor: '#1A1A1A', padding: '100px 0 30px' }}>
        <div className="container">
          <div className="row g-5">
            {/* Brand Column */}
            <div className="col-lg-4">
              <div className="d-flex align-items-center gap-2 mb-4">
                <span style={{ color: '#D4AF37', fontSize: '1.6rem' }}>✦</span>
                <div>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.3rem', color: '#ffffff', display: 'block', lineHeight: 1.2 }}>
                    SYRA FOOD POINT
                  </span>
                  <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', color: '#D4AF37', letterSpacing: '3px', textTransform: 'uppercase' }}>
                    & Grill
                  </span>
                </div>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem', lineHeight: 1.9, marginBottom: '24px', maxWidth: '320px' }}>
                Experience the finest grilled cuisine in Seguku. Where passion meets flavor in every dish we serve.
              </p>
              <div className="d-flex gap-2">
                <a href="https://www.facebook.com/syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-facebook-f"></i></a>
                <a href="https://www.instagram.com/syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-instagram"></i></a>
                <a href="https://www.tiktok.com/@syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-tiktok"></i></a>
                <a href="https://wa.me/256772206129" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-whatsapp"></i></a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2">
              <h5 className="text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.05rem', fontWeight: 600 }}>Quick Links</h5>
              <ul className="list-unstyled">
                {['/', '/menu', '/booking', '/gallery', '/contact'].map((path, i) => (
                  <li key={i} className="mb-3">
                    <Link to={path} className="text-decoration-none" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                      {['Home', 'Menu', 'Reservations', 'Gallery', 'Contact'][i]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Opening Hours */}
            <div className="col-lg-3">
              <h5 className="text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.05rem', fontWeight: 600 }}>Opening Hours</h5>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <span style={{ color: 'rgba(255,255,255,0.85)', display: 'block', marginBottom: '2px', fontSize: '0.9rem' }}>Monday – Friday</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>10:00 AM – 10:00 PM</span>
                </li>
                <li className="mb-3">
                  <span style={{ color: 'rgba(255,255,255,0.85)', display: 'block', marginBottom: '2px', fontSize: '0.9rem' }}>Saturday – Sunday</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>9:00 AM – 11:00 PM</span>
                </li>
              </ul>
              <div className="d-flex align-items-center gap-2 mt-3">
                <MapPin size={14} color="#D4AF37" />
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Seguku, Entebbe Road, Uganda</span>
              </div>
            </div>

            {/* Newsletter + Payment */}
            <div className="col-lg-3">
              <h5 className="text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.05rem', fontWeight: 600 }}>Newsletter</h5>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem', marginBottom: '16px', lineHeight: 1.7 }}>
                Subscribe for special offers and updates
              </p>
              <div className="input-group mb-4">
                <input type="email" className="form-control" placeholder="Your email" style={{
                  backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  color: '#ffffff', borderRadius: '8px 0 0 8px', fontSize: '0.9rem', padding: '12px 16px'
                }} />
                <button className="btn btn-gold" type="button" style={{ borderRadius: '0 8px 8px 0', padding: '12px 20px', fontSize: '0.85rem' }}>
                  Subscribe
                </button>
              </div>
              {/* Payment Methods */}
              <h6 style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, marginBottom: '8px' }}>
                We Accept
              </h6>
              <div className="payment-methods">
                <span>MTN MoMo</span>
                <span>Airtel Money</span>
                <span>Visa</span>
                <span>Cash</span>
              </div>
            </div>
          </div>

          <hr style={{ borderColor: 'rgba(255,255,255,0.08)', margin: '50px 0 30px' }} />

          <div className="row align-items-center">
            <div className="col-md-6">
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: 0 }}>
                &copy; 2024 Syra Food Point & Grill. All rights reserved.
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <button type="button" className="btn btn-link text-decoration-none me-4 p-0 border-0" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Privacy Policy</button>
              <button type="button" className="btn btn-link text-decoration-none p-0 border-0" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Terms of Service</button>
            </div>
          </div>
        </div>
      </footer>

      <FloatingActionMenu />
    </div>
  );
};

export default Home;
